import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TimezoneSelect from "@/components/TimezoneSelect";
import { Pause, Play, Prohibit, UserCheck, Eye, EyeSlash, Warning } from "@phosphor-icons/react";
import { networkLabel } from "@/lib/networkLabels";

// Lowercase keys must match `network` field as written to brands /
// publisher_brands / actions. Order = how they appear in the admin UI.
// Network labels come from @/lib/networkLabels; "manual" is a sentinel.
const NETWORK_OPTION_KEYS = [
  "impact", "skimlinks", "mrge", "partnerize", "yieldkit", "flexoffers", "cj",
  "admedia", "shopnomix", "pronomix", "shopnomix_kevin", "pubadhub",
  "pubadhub_kevin", "siteplug", "maxbounty", "cashbackhive", "fatcoupon",
  "giddyup", "rga",
];
const NETWORK_OPTIONS = [
  ...NETWORK_OPTION_KEYS.map((key) => ({ key, label: networkLabel(key) })),
  { key: "manual", label: "Manual" },
];

// `rga` turns off both RGA accounts; a single account may also be off.
export const RGA_KEYS = ["rga", "rga_vishy", "rga_ashok"];

/**
 * Publisher status header — name, status pill, contact details, sub_id,
 * and the row of admin actions (approve / reject / pause / activate)
 * gated by current status. Plus the click-to-edit Default Split card
 * and the per-pub "Disclose feed networks" opt-in toggle.
 */
export function PublisherHeader({
  publisher,
  publisherDefaultSplit,
  onChangeStatus,
  onUpdateDefaultSplit,
  onToggleDiscloseNetworks,
  onToggleDisabledNetwork,
  onUpdateTimezone,
  onUpdateClickCap,
  splitHistory = [],
}) {
  const status = (publisher.status || "active").toLowerCase();
  const discloseOn = Boolean(publisher.disclose_networks);
  const savedTimezone = publisher.timezone || "UTC";
  const [draftTimezone, setDraftTimezone] = useState(savedTimezone);
  const [savingTimezone, setSavingTimezone] = useState(false);

  const capSummary = publisher.click_cap_summary || {};
  const savedCapRaw = publisher.click_cap_per_day;
  const savedCapMode =
    savedCapRaw === null || savedCapRaw === undefined
      ? "default"
      : savedCapRaw === -1
        ? "unlimited"
        : "custom";
  const savedCapCustom = savedCapMode === "custom" ? String(savedCapRaw) : "1000";
  const savedIncEnabled = Boolean(capSummary.auto_increment_enabled);
  const savedIncPct = Number(capSummary.auto_increment_pct ?? 10);

  const [draftCapMode, setDraftCapMode] = useState(savedCapMode);
  const [draftCapCustom, setDraftCapCustom] = useState(savedCapCustom);
  const [draftIncEnabled, setDraftIncEnabled] = useState(savedIncEnabled);
  const [draftIncPct, setDraftIncPct] = useState(String(savedIncPct > 0 ? savedIncPct : 10));
  const [savingClickCap, setSavingClickCap] = useState(false);
  const [pendingNetwork, setPendingNetwork] = useState(null);
  const [confirmingNetwork, setConfirmingNetwork] = useState(false);

  const disabledList = Array.isArray(publisher.disabled_networks)
    ? publisher.disabled_networks.map((n) => (n || "").toLowerCase())
    : [];
  const pill = {
    active:            { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Active" },
    paused:            { cls: "bg-amber-50 text-amber-700 border-amber-200",       label: "Paused" },
    pending_approval:  { cls: "bg-blue-50 text-blue-700 border-blue-200",          label: "Pending approval" },
    rejected:          { cls: "bg-red-50 text-red-700 border-red-200",             label: "Rejected" },
  }[status] || { cls: "bg-slate-100 text-slate-700 border-slate-200", label: status };

  useEffect(() => {
    setDraftTimezone(savedTimezone);
  }, [savedTimezone]);

  useEffect(() => {
    setDraftCapMode(savedCapMode);
    setDraftCapCustom(savedCapCustom);
    setDraftIncEnabled(savedIncEnabled);
    setDraftIncPct(String(savedIncPct > 0 ? savedIncPct : 10));
  }, [savedCapMode, savedCapCustom, savedIncEnabled, savedIncPct]);

  const timezoneDirty = draftTimezone !== savedTimezone;

  const draftCapValue =
    draftCapMode === "default"
      ? null
      : draftCapMode === "unlimited"
        ? -1
        : Math.max(0, parseInt(draftCapCustom, 10) || 0);
  const draftIncPctNum = Math.max(0, parseFloat(draftIncPct) || 0);
  const clickCapDirty =
    draftCapMode !== savedCapMode
    || (draftCapMode === "custom" && Number(draftCapValue) !== Number(savedCapRaw))
    || draftIncEnabled !== savedIncEnabled
    || (draftIncEnabled && draftIncPctNum !== Number(savedIncPct));

  const handleApplyTimezone = async () => {
    if (!timezoneDirty || !onUpdateTimezone) return;
    setSavingTimezone(true);
    try {
      await onUpdateTimezone(draftTimezone);
    } finally {
      setSavingTimezone(false);
    }
  };

  const closeNetworkConfirm = () => {
    if (confirmingNetwork) return;
    setPendingNetwork(null);
  };

  const confirmDisabledNetwork = async () => {
    if (!pendingNetwork || !onToggleDisabledNetwork) return;
    setConfirmingNetwork(true);
    try {
      await onToggleDisabledNetwork(pendingNetwork.key);
      setPendingNetwork(null);
    } finally {
      setConfirmingNetwork(false);
    }
  };

  const handleApplyClickCap = async () => {
    if (!clickCapDirty || !onUpdateClickCap) return;
    if (draftCapMode === "custom" && (draftCapCustom === "" || Number.isNaN(parseInt(draftCapCustom, 10)))) {
      return;
    }
    if (draftIncEnabled && (draftIncPct === "" || Number.isNaN(parseFloat(draftIncPct)) || draftIncPctNum < 0)) {
      return;
    }
    setSavingClickCap(true);
    try {
      await onUpdateClickCap({
        click_cap_per_day: draftCapValue,
        auto_increment_enabled: draftCapMode === "custom" ? draftIncEnabled : false,
        auto_increment_pct: draftCapMode === "custom"
          ? (draftIncEnabled ? draftIncPctNum : 0)
          : 0,
      });
    } finally {
      setSavingClickCap(false);
    }
  };

  const fmtCap = (v) => {
    if (v === null || v === undefined) return "—";
    if (Number(v) === -1) return "∞";
    return Number(v).toLocaleString();
  };
  const originalCap = capSummary.original_click_cap_per_day;
  const currentCap = capSummary.current_click_cap_per_day;
  const sourceLabel = ({
    click_cap_rules: "Click+Sale Caps rule",
    publisher_brands_override: "Assignment override",
    brand_default: "Brand default",
    publisher_default: "Publisher default",
    system_default: "System default (no limit)",
  })[capSummary.source] || (capSummary.source || "—");

  return (
    <div className="mb-6">
    <div>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Publisher Account
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">{publisher.company_name}</h1>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${pill.cls}`} data-testid="pub-status-pill">
              {pill.label}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-2">{publisher.first_name} {publisher.last_name} &middot; {publisher.email}</p>
          <p className="text-xs font-mono text-[#002FA7] font-bold mt-1">SubID: {publisher.sub_id}</p>

          {/* Status action row */}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {status === "pending_approval" && (
              <>
                <Button
                  size="sm"
                  onClick={() => onChangeStatus("approve")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                  data-testid="pub-approve-btn"
                >
                  <UserCheck size={14} className="mr-1.5" weight="bold" />
                  Approve publisher
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onChangeStatus("reject")}
                  className="border-red-200 text-red-700 hover:bg-red-50 h-9"
                  data-testid="pub-reject-btn"
                >
                  <Prohibit size={14} className="mr-1.5" weight="bold" />
                  Reject
                </Button>
              </>
            )}
            {status === "active" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onChangeStatus("pause")}
                className="border-amber-200 text-amber-700 hover:bg-amber-50 h-9"
                data-testid="pub-pause-btn"
                title="Block login and API access for this publisher"
              >
                <Pause size={14} className="mr-1.5" weight="bold" />
                Pause publisher
              </Button>
            )}
            {status === "paused" && (
              <Button
                size="sm"
                onClick={() => onChangeStatus("activate")}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white h-9"
                data-testid="pub-activate-btn"
              >
                <Play size={14} className="mr-1.5" weight="bold" />
                Re-activate publisher
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-stretch gap-3 shrink-0">
          <button
            type="button"
            onClick={() => onToggleDiscloseNetworks && onToggleDiscloseNetworks(!discloseOn)}
            className={`border rounded-md px-4 py-3 text-left transition-all hover:border-[#002FA7] ${
              discloseOn
                ? "bg-[#002FA7]/5 border-[#002FA7]/40"
                : "bg-white border-slate-200"
            }`}
            title={
              discloseOn
                ? "ON — this pub's API includes the upstream feed network on every brand row. Click to hide."
                : "OFF — the upstream feed (Impact/Skimlinks/mrge/...) is hidden from this pub's API. Click to expose."
            }
            data-testid="disclose-networks-toggle"
          >
            <div className="flex items-center gap-1.5">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
                Feed Network
              </div>
              {discloseOn ? (
                <Eye size={12} weight="bold" className="text-[#002FA7]" />
              ) : (
                <EyeSlash size={12} weight="bold" className="text-slate-400" />
              )}
            </div>
            <div
              className={`font-heading text-sm font-bold mt-1 ${
                discloseOn ? "text-[#002FA7]" : "text-slate-400"
              }`}
              data-testid="disclose-networks-state"
            >
              {discloseOn ? "Disclosed" : "Hidden"}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5 max-w-[120px] leading-tight">
              {discloseOn ? "in API & UI" : "click to expose"}
            </div>
          </button>

          <div
            className="border border-slate-200 rounded-md px-4 py-3 text-right cursor-pointer hover:border-[#002FA7] transition-colors"
            onClick={onUpdateDefaultSplit}
            data-testid="default-split-card"
          >
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Default Split</div>
            <div className="font-heading text-xl font-bold text-[#002FA7] mt-0.5 font-mono">
              {publisherDefaultSplit}/{100 - publisherDefaultSplit}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">Click to edit · new offers only</div>
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-slate-500 leading-relaxed max-w-2xl ml-auto text-right" data-testid="default-split-policy-hint">
        Changing the default applies to <b>new</b> brand assignments only.
        Existing offers keep their current split — edit those brand-by-brand in Assigned Brands.
      </p>

      {splitHistory.length > 0 && (
        <div
          className="mt-3 border border-slate-200 rounded-md p-3 bg-slate-50/60"
          data-testid="default-split-history"
        >
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
            Default split history
          </div>
          <ul className="space-y-1.5 max-h-36 overflow-y-auto">
            {splitHistory.slice(0, 10).map((h, idx) => {
              const from = Number(h.from_split_pct);
              const to = Number(h.to_split_pct);
              const when = h.changed_at
                ? new Date(h.changed_at).toLocaleString()
                : "—";
              return (
                <li
                  key={`${h.changed_at}-${idx}`}
                  className="text-[11px] text-slate-600 flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
                  data-testid={`default-split-history-row-${idx}`}
                >
                  <span className="font-mono font-semibold text-slate-800">
                    {from}/{100 - from} → {to}/{100 - to}
                  </span>
                  <span className="text-slate-400">{when}</span>
                  {h.changed_by_email && (
                    <span className="text-slate-500">by {h.changed_by_email}</span>
                  )}
                  {h.note ? <span className="text-slate-400 italic">— {h.note}</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>

    {/* Timezone — drives clicks-log day bounds + click-cap reset */}
    <div
      className="mt-4 border border-slate-200 rounded-md p-4"
      data-testid="publisher-timezone-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-600 mb-1">
            Timezone
          </div>
          <p className="text-xs text-slate-500 leading-relaxed max-w-xl">
            Controls clicks-log date filters/timestamps and daily click-cap reset for this publisher.
            Existing accounts without a saved timezone stay on UTC. Select a timezone, then click Apply.
          </p>
        </div>
        <div className="w-full sm:w-[28rem] shrink-0 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <TimezoneSelect
              value={draftTimezone}
              onChange={setDraftTimezone}
              listEndpoint="/admin/timezones"
              disabled={savingTimezone}
              testId="admin-pub-timezone-select"
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="h-10 px-4 shrink-0 bg-[#002FA7] hover:bg-[#00227A] text-white"
            disabled={!timezoneDirty || savingTimezone}
            onClick={handleApplyTimezone}
            data-testid="admin-pub-timezone-apply"
          >
            {savingTimezone ? "Saving…" : "Apply"}
          </Button>
        </div>
      </div>
    </div>

    {/* Publisher-level daily click cap */}
    <div
      className="mt-4 border border-slate-200 rounded-md p-4 space-y-4"
      data-testid="admin-pub-click-cap-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-600 mb-1">
            Daily click cap
          </div>
          <p className="text-xs text-slate-500 leading-relaxed max-w-xl">
            Apply resets the live cap and clears this publisher's network/brand
            Click+Sale Caps rules so
            <span className="font-mono text-[11px]"> /v1/brands </span>
            matches immediately. Brand-level caps still win when set. Weekly
            auto-increment grows from the current value. Per brand per day
            (publisher timezone midnight).
          </p>
        </div>
        <div className="w-full sm:w-[28rem] shrink-0 space-y-3">
          <div className="flex items-center gap-2">
            <select
              className="h-10 flex-1 min-w-0 rounded-md border border-slate-200 bg-white pl-3 pr-10 text-sm text-slate-800 appearance-none bg-[length:12px_12px] bg-[right_0.75rem_center] bg-no-repeat"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E\")",
              }}
              value={draftCapMode}
              disabled={savingClickCap}
              onChange={(e) => setDraftCapMode(e.target.value)}
              data-testid="admin-pub-click-cap-mode"
            >
              <option value="default">Default (no limit)</option>
              <option value="custom">Custom</option>
              <option value="unlimited">No limit</option>
            </select>
            {draftCapMode === "custom" && (
              <input
                type="number"
                min={0}
                className="h-10 w-28 shrink-0 rounded-md border border-slate-200 bg-white px-3 text-sm font-mono"
                value={draftCapCustom}
                disabled={savingClickCap}
                onChange={(e) => setDraftCapCustom(e.target.value)}
                data-testid="admin-pub-click-cap-custom"
              />
            )}
            <Button
              type="button"
              size="sm"
              className="h-10 px-4 shrink-0 bg-[#002FA7] hover:bg-[#00227A] text-white"
              disabled={!clickCapDirty || savingClickCap}
              onClick={handleApplyClickCap}
              data-testid="admin-pub-click-cap-apply"
            >
              {savingClickCap ? "Saving…" : "Apply / reset"}
            </Button>
          </div>
          {draftCapMode === "custom" && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2"
              data-testid="admin-pub-cap-increment-controls"
            >
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={draftIncEnabled}
                  disabled={savingClickCap}
                  onChange={(e) => setDraftIncEnabled(e.target.checked)}
                  data-testid="admin-pub-cap-inc-enabled"
                />
                Weekly auto-increment
              </label>
              {draftIncEnabled && (
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <span>+%</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="h-8 w-20 rounded-md border border-slate-200 bg-white px-2 text-sm font-mono"
                    value={draftIncPct}
                    disabled={savingClickCap}
                    onChange={(e) => setDraftIncPct(e.target.value)}
                    data-testid="admin-pub-cap-inc-pct"
                  />
                  <span className="text-slate-400">/ week</span>
                </label>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs"
        data-testid="admin-pub-click-cap-summary"
      >
        <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Original</div>
          <div className="font-mono text-sm text-slate-800 mt-0.5" data-testid="admin-pub-cap-original">
            {fmtCap(originalCap)}
          </div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Current (live)</div>
          <div className="font-mono text-sm text-[#002FA7] font-bold mt-0.5" data-testid="admin-pub-cap-current">
            {fmtCap(currentCap)}
          </div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Source</div>
          <div className="text-sm text-slate-700 mt-0.5" data-testid="admin-pub-cap-source">
            {sourceLabel}
            {capSummary.last_increment_at ? (
              <span className="text-slate-400 ml-1">
                · last bump {new Date(capSummary.last_increment_at).toLocaleDateString()}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>

    {/* Disabled Networks chip row */}
    <div
      className="mt-4 border border-slate-200 rounded-md p-4"
      data-testid="disabled-networks-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Warning size={14} weight="bold" className="text-amber-500" />
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-600">
              Disabled Networks
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Hard kill-switch for this publisher only. Brands on a disabled network are
            paused for this pub and stripped from <span className="font-mono text-[11px]">/v1/brands</span>,
            commission & conversion feeds, and the <span className="font-mono text-[11px]">/click</span> tracker
            (returns 403). Click a chip, then confirm.
          </p>
        </div>
        <div className="text-[10px] text-slate-400 shrink-0">
          {disabledList.length === 0
            ? "All networks active"
            : `${disabledList.length} disabled`}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {NETWORK_OPTIONS.map((net) => {
          const isDisabled = net.key === "rga"
            ? disabledList.some((n) => RGA_KEYS.includes(n))
            : disabledList.includes(net.key);
          return (
            <button
              key={net.key}
              type="button"
              onClick={() => setPendingNetwork({ key: net.key, label: net.label, isDisabled })}
              className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
                isDisabled
                  ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100 line-through"
                  : "bg-white border-slate-200 text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"
              }`}
              title={
                isDisabled
                  ? `Re-enable ${net.label} for this publisher (restores cascade-paused offers)`
                  : `Disable ${net.label} — pauses this pub's offers on that network and blocks API/tracker traffic`
              }
              data-testid={`disabled-network-chip-${net.key}`}
            >
              {isDisabled ? "✕ " : ""}
              {net.label}
            </button>
          );
        })}
      </div>
      <AlertDialog
        open={Boolean(pendingNetwork)}
        onOpenChange={(open) => { if (!open) closeNetworkConfirm(); }}
      >
        <AlertDialogContent data-testid="disabled-network-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingNetwork?.isDisabled
                ? `Re-enable ${pendingNetwork.label}?`
                : `Disable ${pendingNetwork?.label}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingNetwork?.isDisabled
                ? `This restores cascade-paused ${pendingNetwork.label} offers for this publisher.`
                : `This pauses this publisher's ${pendingNetwork?.label} offers and blocks API/tracker traffic for that network.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row justify-end gap-2 sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              onClick={confirmDisabledNetwork}
              disabled={confirmingNetwork}
              data-testid="disabled-network-confirm-btn"
            >
              {confirmingNetwork ? "Working…" : "Confirm"}
            </Button>
            <Button
              type="button"
              onClick={closeNetworkConfirm}
              disabled={confirmingNetwork}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="disabled-network-cancel-btn"
            >
              Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </div>
  );
}
